import { Editor } from '@tiptap/core';
import { Editor as ReactEditor } from '@tiptap/react';
import cx from 'classnames';
import { useAuthContext } from 'components/auth/context';
import { CourseVariablesModal } from 'components/internal/cms/NudgeEditor/BlockEditor/CourseVariablesModal';
import { DropdownMenu, KebabMenu } from 'components/ui';
import { Check } from 'ethena-icons';
import { useToggle } from 'lib/hooks';
import _ from 'lodash';
import { useMemo, useState } from 'react';
import { LinkModal } from './LinkModal';
import css from './Menu.module.scss';
import { useRichTextEditorContext } from './context';
import {
  CUSTOM_ITEMS,
  getMenuItems,
  IMenuButtonConfiguration,
  IMenuSelectConfiguration,
  IMenuUIElementConfiguration,
  RichTextEditorMenuItemConfiguration,
} from './datasets';
import {
  RichTextEditorButton,
  RichTextEditorCustomItem,
  RichTextEditorSelect,
  RichTextEditorUIElement,
} from './types';

export interface IMenuProps {
  buttonClassName?: string;
  className?: string;
  courseId?: number;
  orderedItems: RichTextEditorMenuItemConfiguration[];
  includedCustomItems?: RichTextEditorCustomItem[];
}

export const Menu = ({
  buttonClassName,
  className,
  courseId,
  orderedItems,
  includedCustomItems,
}: IMenuProps) => {
  const { editor } = useRichTextEditorContext();
  const { session } = useAuthContext();
  const isHotlineEnabled =
    session?.configurations?.isMisconductReportingEnabled;
  const [isVariablesModalVisible, toggleVariablesModal] = useToggle(false);
  const [isLinkModalVisible, toggleLinkModal] = useToggle(false);
  const [link, setLink] = useState<string>('');

  const handleSetLink = (href: string) => {
    if (href) {
      editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
    }
    toggleLinkModal();
  };

  const handleSetLinkClick = (editor: Editor) => {
    setLink(editor.getAttributes('link').href);
    toggleLinkModal();
  };

  const renderMenuItem = (
    menuItem:
      | IMenuButtonConfiguration
      | IMenuSelectConfiguration
      | IMenuUIElementConfiguration
  ) => {
    if (menuItem.id in RichTextEditorButton) {
      const {
        getIsDisabled,
        getIsSelected,
        icon: Icon,
        id,
        onClick,
      } = menuItem as IMenuButtonConfiguration;

      const handleClick = () => {
        const cb =
          id === RichTextEditorButton.SET_LINK ? handleSetLinkClick : undefined;
        onClick(editor, cb);
      };

      const isDisabled = getIsDisabled?.(editor);

      return (
        <button
          aria-disabled={isDisabled}
          className={cx(css.menuButton, buttonClassName, {
            [css.selected]: getIsSelected?.(editor),
          })}
          key={id}
          onClick={(ev) => (isDisabled ? ev.preventDefault() : handleClick())}
          type="button"
        >
          <Icon height={18} width={18} />
        </button>
      );
    }

    if (menuItem.id in RichTextEditorUIElement) {
      return <VerticalSeparator />;
    }

    if (menuItem.id in RichTextEditorSelect) {
      const { id, options } = menuItem as IMenuSelectConfiguration;

      const selected =
        _.find(options, (x) => x.getIsSelected(editor)) || _.first(options);
      const isTextStyleSelect = id === RichTextEditorSelect.TEXT_STYLE;

      return (
        <DropdownMenu key={id}>
          <DropdownMenu.Toggle
            className={cx(css.menuSelectToggle, {
              [css.textStyle]: isTextStyleSelect,
            })}
          >
            {selected?.getLabel(options)}
          </DropdownMenu.Toggle>
          <DropdownMenu.Options
            aria-label="Text styles menu"
            className={cx(css.menuSelectOptions, {
              [css.textStyle]: isTextStyleSelect,
            })}
          >
            {_.map(options, (option) => (
              <DropdownMenu.Option
                className={cx(css.menuSelectOption, css[option.classNameKey])}
                key={option.id}
                onClick={() => option.onSelect(editor)}
              >
                {option.getLabel(options)}
                {option.getIsSelected(editor) && (
                  <Check
                    aria-hidden="true"
                    className={css.checkIcon}
                    height={12}
                    width={12}
                  />
                )}
              </DropdownMenu.Option>
            ))}
          </DropdownMenu.Options>
        </DropdownMenu>
      );
    }
  };

  const renderCustomItems = () => {
    const kebabMenuItems = _.map(CUSTOM_ITEMS, (x) => x.id);
    let includedKebabMenuItems = _.intersection(
      kebabMenuItems,
      includedCustomItems
    );

    if (!isHotlineEnabled) {
      includedKebabMenuItems = _.without(
        includedKebabMenuItems,
        RichTextEditorCustomItem.HOTLINE_CTA
      );
    }

    if (_.isEmpty(includedKebabMenuItems)) {
      return null;
    }

    return (
      <KebabMenu>
        <KebabMenu.Toggle
          className={css.kebabMenuToggle}
          openClassName={css.kebabMenuToggleOpen}
        />
        <KebabMenu.Options className={css.kebabMenuOptions}>
          {_.map(includedKebabMenuItems, (menuItem) => {
            const item = _.find(CUSTOM_ITEMS, (x) => x.id === menuItem);
            const handleClick = () => {
              if (menuItem === RichTextEditorCustomItem.VARIABLES) {
                toggleVariablesModal();
              } else {
                item.onClick(editor);
              }
            };
            return (
              <KebabMenu.Option key={item.id} onClick={handleClick}>
                {item.label}
              </KebabMenu.Option>
            );
          })}
        </KebabMenu.Options>
      </KebabMenu>
    );
  };

  const menuItems = useMemo(() => getMenuItems(orderedItems), [orderedItems]);

  return (
    <div className={cx(css.menu, className)}>
      {_.map(menuItems, renderMenuItem)}
      {renderCustomItems()}
      <CourseVariablesModal
        courseId={courseId}
        editor={editor as ReactEditor}
        isVisible={isVariablesModalVisible}
        onHide={toggleVariablesModal}
      />
      <LinkModal
        isVisible={isLinkModalVisible}
        onClose={toggleLinkModal}
        onConfirm={handleSetLink}
        value={link}
      />
    </div>
  );
};

const VerticalSeparator = () => {
  return <div className={css.separator} aria-hidden="true" />;
};
